import { jsx as _jsx } from "preact/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, } from 'preact/compat';
import { useState } from 'react';
import { FormStage } from '@genericBookings/types/FormStage';
const StepCompletionContext = createContext({
    steps: [],
    modifyStep: (step, stepComplete, stepViewed, stepInitialised) => null,
    completedSteps: [],
    dateStep: {},
    activitiesStep: {},
    attendeesStep: {},
    sessionStep: {},
    incompleteSteps: [],
    resetStepsCompleted: () => null,
    isCompleted: false,
    isLastStep: false,
    step: {},
    stepName: '',
});
export function useStepCompletion() {
    const value = useContext(StepCompletionContext);
    return value ?? {};
}
export function StepCompletionProvider({ children, isBuilder }) {
    const [dateStep, setDateStep] = useState({
        step: FormStage.Date,
        name: 'calendar',
        isComplete: false,
        required: true,
        isViewed: false,
        isInitialised: false,
    });
    const [activitiesStep, setActivitiesStep] = useState({
        step: FormStage.Activities,
        name: 'activities',
        required: isBuilder,
        isViewed: false,
        isInitialised: false,
        isComplete: false,
    });
    const [attendeesStep, setAttendeesStep] = useState({
        step: FormStage.Attendees,
        name: 'attendees',
        required: !isBuilder,
        isViewed: false,
        isInitialised: false,
        isComplete: false,
    });
    const [sessionStep, setSessionStep] = useState({
        step: FormStage.Session,
        name: 'sessions',
        required: true,
        isViewed: false,
        isInitialised: false,
        isComplete: false,
    });
    const steps = useMemo(() => [dateStep, activitiesStep, attendeesStep, sessionStep].filter((step) => step.required), [activitiesStep, attendeesStep, dateStep, sessionStep]);
    const determineStepSetter = useCallback((step) => {
        switch (step.step) {
            case FormStage.Attendees:
                return setAttendeesStep;
            case FormStage.Session:
                return setSessionStep;
            case FormStage.Activities:
                return setActivitiesStep;
            default:
                return setDateStep;
        }
    }, [setAttendeesStep, setSessionStep, setActivitiesStep, setDateStep]);
    const modifyStep = (step, complete, viewed, initialised) => {
        const setStep = determineStepSetter(step);
        setStep({
            ...step,
            isComplete: complete,
            isViewed: viewed !== undefined ? viewed : step.isViewed,
            isInitialised: initialised !== undefined ? initialised : step.isInitialised,
        });
    };
    const isCompleted = useMemo(() => steps.filter((step) => !step.isComplete).length === 0, [steps]);
    const calculateStep = useCallback(() => steps.find((step) => !step.isComplete) ?? sessionStep, [sessionStep, steps]);
    const isLastStep = useMemo(() => steps.filter((step) => !step.isComplete).length === 1, [steps]);
    const resetStepsCompleted = useCallback(() => {
        steps.forEach((step) => {
            modifyStep(step, false);
        });
    }, [steps, modifyStep]);
    const value = useMemo(() => ({
        steps,
        modifyStep,
        completedSteps: steps.filter((step) => step.isComplete),
        incompleteSteps: steps.filter((step) => !step.isComplete),
        dateStep,
        activitiesStep,
        attendeesStep,
        sessionStep,
        isCompleted,
        isLastStep,
        resetStepsCompleted,
        step: calculateStep(),
        stepName: calculateStep()?.name ?? 'completed',
    }), [steps, modifyStep, dateStep, activitiesStep, attendeesStep, sessionStep, isCompleted, isLastStep, resetStepsCompleted, calculateStep]);
    return (_jsx(StepCompletionContext.Provider, { value: value, children: children }));
}
